import { stringify } from 'query-string';
import {
    fetchUtils,
    GET_LIST,
    GET_ONE,
    GET_MANY,
    GET_MANY_REFERENCE,
    CREATE,
    UPDATE,
    UPDATE_MANY,
    DELETE,
    DELETE_MANY,
} from 'react-admin';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import has from 'lodash/has';

const convertToWaterline = (filters) => {

    let where = {}
    const serverLog = filters['serverLog'] ? true : false;
    const messagesKey = filters['messages'] ? true : false;

    forEach(filters, (value, key) => {
        if (key.endsWith("_id") || typeof value !== "string" || serverLog || messagesKey) {
            where[key] = key === ('serverLog' || 'messages') ? null : value
        } else {
            const words = value.trim().split(" ")
            if (words.length === 1) {
                where[key] = { contains: words[0] }
            } else {
                where["and"] = words.map(word => ({ [key]: { contains: word } }))
            }
        }
    })

    return where
}

/**
 * Maps react-admin queries to a json-server powered REST API
 *
 * @example
 * GET_LIST     => GET http://my.api.url/posts?_sort=title%20ASC&skip=0&limit=24
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts/?where={"id": [123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts/123
 * DELETE       => DELETE http://my.api.url/posts/123
 */
export default (apiUrl, httpClient = fetchUtils.fetchJson) => {
    /**
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} { url, options } The HTTP request parameters
     */
    const convertDataRequestToHTTP = (type, resource, params) => {
        let url = '';
        const options = {};
        switch (type) {
            case GET_LIST: {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;
                const { groupBy, count, max, ...filters } = params.filter;

                const where = convertToWaterline(filters)
                const query = {
                    where: JSON.stringify(where),
                    groupBy,
                    count,
                    max,
                    sort: `${field} ${order}`,
                    skip: (page - 1) * perPage,
                    limit: perPage,
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case GET_ONE:
                if (params) {
                    url = `${apiUrl}/${resource}/${params.id}`;
                    console.log(`${resource}/${params.id} => Calling GET_ONE`);
                } else {
                    url = `${apiUrl}/${resource}`;
                    console.log(`${resource} => Calling GET_ONE`);
                }
                break;
            case GET_MANY_REFERENCE: {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;
                const { groupBy, count, max, ...filters } = params.filter;
                const where = convertToWaterline(filters)
                where[params.target] = params.id

                const query = {
                    where: JSON.stringify(where),
                    groupBy,
                    count,
                    max,
                    sort: `${field} ${order}`,
                    skip: (page - 1) * perPage,
                    limit: perPage
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case UPDATE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'PUT';
                options.body = JSON.stringify(params.data);
                break;
            case CREATE:
                url = `${apiUrl}/${resource}`;
                options.method = 'POST';
                options.body = JSON.stringify(params.data);
                break;
            case DELETE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'DELETE';
                break;
            case 'DELETE_MULTI':
                url = `${apiUrl}/${resource}`;
                options.body = JSON.stringify(params.data);
                options.method = 'DELETE';
                break;
            case 'UPLOAD':
                url = `${apiUrl}/${resource}`;
                options.method = 'POST';
                options.body = params;
                break;
            case 'PUBLISH':
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'POST';
                options.body = JSON.stringify(params.data);
                break;
            case GET_MANY: {
                const idsToLookOut = map(params.ids, look => typeof look === "object" ? look.id : look);
                const query = {
                    where: JSON.stringify({ id: idsToLookOut })
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                console.log(`${resource}?${stringify(query)} => Calling GET_MANY`);
                break;
            }
            default:
                throw new Error(`Unsupported fetch action type ${type}`);
        }
        return { url, options };
    };

    /**
     * @param {Object} response HTTP response from fetch()
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} Data response
     */
    const convertHTTPResponse = (response, type, resource, params) => {
        let { headers, json } = response;
        switch (type) {
            case GET_LIST:
            case GET_MANY_REFERENCE:

                if (!headers.has('x-total-count')) {
                    throw new Error(
                        'The X-Total-Count header is missing in the HTTP Response. The jsonServer Data Provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare X-Total-Count in the Access-Control-Expose-Headers header?'
                    );
                }
                if (Array.isArray(json) && !has(json[0], "id")) json = map(json, (item, ix) => ({
                    id: ix,
                    ...item
                }));
                return {
                    data: json.data,
                    success: json.success,
                    message: json.message,
                    // total: 4
                    total: parseInt(
                        headers
                            .get('x-total-count')
                            .split('/')
                            .pop(),
                        10
                    ),
                };
            case CREATE:
                return {
                    data: json.data,
                    success: json.success,
                    message: json.message
                };
            default:
                return {
                    data: json.data,
                    success: json.success,
                    message: json.message
                };
        }
    };

    /**
     * @param {string} type Request type, e.g GET_LIST
     * @param {string} resource Resource name, e.g. "posts"
     * @param {Object} payload Request parameters. Depends on the request type
     * @returns {Promise} the Promise for a data response
     */
    return (type, resource, params) => {
        // json-server doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
        if (type === UPDATE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${resource}/${id}`, {
                        method: 'PATCH',
                        body: JSON.stringify(params.data),
                    })
                )
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }
        // json-server doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
        if (type === DELETE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${resource}/${id}`, {
                        method: 'DELETE',
                    })
                )
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }
        const { url, options } = convertDataRequestToHTTP(
            type,
            resource,
            params
        );
        return httpClient(url, options).then(response =>
                convertHTTPResponse(response, type, resource, params)

        );
    };
};
